import { Button } from '@forwardflow/ui/Button'
import { message, Modal } from 'antd'
import { useState } from 'react'
import { UserService } from '@forwardflow/services/UserService'
import Icon from '@forwardflow/ui/Icons'

interface ResendInvitationModalProps {
   isOpen: boolean
   onClose: () => void
   userEmail: string
   userName: string
   role: 'Admin' | 'Agent'
   linkedEmail?: string
}

export const ResendInvitationModal: React.FC<ResendInvitationModalProps> = ({
   isOpen,
   onClose,
   userEmail,
   userName,
   role,
   linkedEmail: existingLinkedEmail,
}) => {
   const [isLoading, setIsLoading] = useState(false)
   const [isSuccess, setIsSuccess] = useState(false)
   const [selectedRole, setSelectedRole] = useState<typeof role>(role || 'Admin')
   const [loginEmail, setLoginEmail] = useState(existingLinkedEmail || '')

   const handleResend = async () => {
      setIsLoading(true)
      try {
         const trimmed = loginEmail.trim()
         // If login email is set → link it. If cleared (was set before) → send null to remove.
         const linkedEmails = trimmed
            ? { [userEmail]: trimmed }
            : existingLinkedEmail
               ? { [userEmail]: null as any }
               : undefined
         await UserService.userSendInvitation([userEmail], selectedRole, linkedEmails)
         message.success('Invitation resent successfully')
         setIsSuccess(true)
         setTimeout(() => {
            handleClose()
         }, 2000)
      } catch (error) {
         console.error(error)
         message.error('Failed to resend invitation')
      } finally {
         setIsLoading(false)
      }
   }

   const handleClose = () => {
      setIsSuccess(false)
      setSelectedRole(role || 'Admin')
      setLoginEmail(existingLinkedEmail || '')
      onClose()
   }

   const SuccessComponent = () => {
      return (
         <div className="flex flex-col gap-y-5 !bg-tile_1_bg_img">
            <div className="w-[100px] h-[100px] rounded-full bg-primary/40 flexc mx-auto mt-[110px]">
               <Icon name="Check" className="scale-[5] fill-white" />
            </div>
            <p className="mb-20 text-[24px] font-semibold text-center">
               Invitation Resent!
            </p>
         </div>
      )
   }

   return (
      <Modal
         className={`${!isSuccess ? '' : 'p-0'} ff-modal lg:!w-[480px]`}
         title={!isSuccess ? 'Resend Invitation' : ''}
         open={isOpen}
         onCancel={handleClose}
         footer={null}
      >
         {isSuccess ? (
            <SuccessComponent />
         ) : (
            <div className="flex flex-col gap-y-4">
               <p className="text-[14px] text-white/80">
                  Are you sure you want to resend the invitation to{' '}
                  <span className="font-semibold text-white">{userName}</span> (
                  {userEmail})?
               </p>
               
               <div>
                  <p className="text-[#818181] text-[12px] font-medium mb-3">
                     Select Role
                  </p>
                  <div className="flex gap-4">
                     <label className={`flex items-center gap-2 ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                           type="radio"
                           name={`resend-role-${userEmail}`}
                           value="Admin"
                           checked={selectedRole === 'Admin'}
                           onChange={(e) => setSelectedRole(e.target.value as 'Admin' | 'Agent')}
                           disabled={isLoading}
                           className="w-4 h-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
                        />
                        <span className="text-[14px] font-medium text-white">Admin</span>
                     </label>
                     <label className={`flex items-center gap-2 ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                           type="radio"
                           name={`resend-role-${userEmail}`}
                           value="Agent"
                           checked={selectedRole === 'Agent'}
                           onChange={(e) => setSelectedRole(e.target.value as 'Admin' | 'Agent')}
                           disabled={isLoading}
                           className="w-4 h-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
                        />
                        <span className="text-[14px] font-medium text-white">Agent</span>
                     </label>
                  </div>
               </div>

               <div className="mt-3">
                  <p className="text-[#818181] text-[12px] font-medium mb-2">
                     Login Email (optional)
                  </p>
                  <p className="text-[11px] text-white/40 mb-2">
                     Set a different email for login. Leave empty to use the CRM email.
                  </p>
                  <input
                     type="email"
                     placeholder={userEmail}
                     value={loginEmail}
                     onChange={(e) => setLoginEmail(e.target.value)}
                     disabled={isLoading}
                     className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/30 outline-none focus:border-primary/50 transition-colors disabled:opacity-50"
                  />
               </div>

               <div className="flex gap-3 mt-2">
                  <Button
                     onClick={handleClose}
                     className="!bg-white/10 hover:!bg-white/20 flex-1"
                     isDisabled={isLoading}
                  >
                     Cancel
                  </Button>
                  <Button
                     onClick={handleResend}
                     isLoading={isLoading}
                     className="flex-1"
                  >
                     Resend Invitation
                  </Button>
               </div>
            </div>
         )}
      </Modal>
   )
}

